import React, { useCallback, useState, useEffect, useContext, useRef, useMemo } from "react";
import { useDropzone } from 'react-dropzone';
import 'trix';
import 'trix/dist/trix.css';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';
import ListBoxQueryMultiSelectEmployeesActiveContract from './list_box_query_employee_active_contract';
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { companyVehiclesList, companyVehicleCreate } from '../services/api_vehicle_employee';

const FUEL_CHOICES = [
  ['benzina', 'Benzina'],
  ['diesel', 'Diesel'],
  ['gpl', 'GPL'],
  ['metano', 'Metano'],
  ['ibrida', 'Ibrida'],
  ['elettrica', 'Elettrica'],
  ['altro', 'Altro'],
];

const FormCreaRimborsoKmCompany = ({ submitForm, afterSubmit }) => {
  const { token } = useContext(AuthContext);

  // -------- Stato form --------
  const [file, setFile] = useState(null);
  const [formValues, setFormValues] = useState({
    title: '',
    date: '',
    // campi veicolo (solo se creo un veicolo nuovo)
    vehicleBrand: '',
    vehicleType: '',
    vehicleMotor: '',
    licensePlate: '',
    vehicleRatePerKm: '',      // <= tariffa anagrafica del veicolo (solo in creazione veicolo)
    // campi refund (sempre)
    requestRatePerKm: '',      // <= tariffa override per questa richiesta (opzionale!)
    departureAddress: '',
    arrivalAddress: '',
    distanceKm: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const descriptionRef = useRef(null);

  // -------- Dipendente & veicoli --------
  const [selectedEmployee, setSelectedEmployee] = useState({ id: "", name: "" });
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(""); // se valorizzato → useremo questo PK

  // -------- helpers --------
  const normalizePlate = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'licensePlate' ? normalizePlate(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: next }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const isEmployeeSelected = Boolean(selectedEmployee?.id);

  const validateForm = () => {
    const errors = {};
    // obbligatori sempre
    if (!formValues.title) errors.title = 'Oggetto è richiesto';
    if (!formValues.date) errors.date = 'Data è richiesta';
    if (!formValues.departureAddress) errors.departureAddress = 'Indirizzo Partenza è richiesto';
    if (!formValues.arrivalAddress) errors.arrivalAddress = 'Indirizzo Arrivo è richiesto';
    if (!formValues.distanceKm) errors.distanceKm = 'Distanza Km è richiesta';
    if (!selectedEmployee.id) errors.employee = 'Selezionare un dipendente';

    // se NON scelgo un veicolo esistente → devo inserire i campi minimi per crearne uno
    if (!selectedVehicleId) {
      if (!formValues.licensePlate) errors.licensePlate = 'Targa è richiesta';
      if (!formValues.vehicleBrand) errors.vehicleBrand = 'Marca è richiesta';
      if (!formValues.vehicleType) errors.vehicleType = 'Modello è richiesto';
      if (!formValues.vehicleMotor) errors.vehicleMotor = 'Alimentazione è richiesta';
      // vehicleRatePerKm resta opzionale
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // -------- Upload allegato (della RICHIESTA) --------
  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile?.size > 2 * 1024 * 1024) {
      Swal.fire('Errore', 'File troppo grande. Massimo 2MB', 'warning');
      return;
    }
    if (selectedFile) setFile(selectedFile);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });
  const handleDeleteFile = () => setFile(null);

  // -------- Caricamento veicoli al cambio dipendente --------
  useEffect(() => {
    (async () => {
      // reset sempre i campi veicolo quando cambia dipendente
      setSelectedVehicleId("");
      setFormValues((prev) => ({
        ...prev,
        licensePlate: "",
        vehicleBrand: "",
        vehicleType: "",
        vehicleMotor: "",
        vehicleRatePerKm: "",
      }));

      if (!selectedEmployee?.id) {
        setVehicles([]);
        return;
      }
      try {
        const res = await companyVehiclesList(token, { employee: selectedEmployee.id, pageSize: 100 });
        const list = res?.results ?? [];
        setVehicles(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setVehicles([]);
      }
    })();
  }, [token, selectedEmployee?.id]);

  // -------- Listbox veicoli --------
  const vehiclePlaceholder = { id: "", name: "Seleziona un veicolo" };
  const vehicleOptions = useMemo(
    () => [
      vehiclePlaceholder,
      ...(vehicles || []).map((v) => ({
        id: String(v.id),
        name: `${v.license_plate} — ${v.vehicle_brand}${v.vehicle_type ? ` ${v.vehicle_type}` : ''}${v.rate_per_km != null ? ` • ${Number(v.rate_per_km).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €/km` : ''}`,
        v,
      })),
    ],
    [vehicles]
  );
  const selectedVehicleOption =
    vehicleOptions.find((o) => String(o.id) === String(selectedVehicleId)) || vehiclePlaceholder;

  const onPickVehicle = (opt) => {
    const pickedId = opt?.id || "";
    setSelectedVehicleId(pickedId);

    if (opt?.v) {
      const v = opt.v;
      // autopopolo i campi per visibilità; quelli veicolo restano disabilitati se scelgo un veicolo esistente
      setFormValues((prev) => ({
        ...prev,
        licensePlate: v.license_plate || "",
        vehicleBrand: v.vehicle_brand || "",
        vehicleType: v.vehicle_type || "",
        vehicleMotor: v.vehicle_motor || "",
        vehicleRatePerKm: v.rate_per_km != null ? String(v.rate_per_km) : "",
      }));
    } else {
      // placeholder → svuota i campi veicolo
      setFormValues((prev) => ({
        ...prev,
        licensePlate: "",
        vehicleBrand: "",
        vehicleType: "",
        vehicleMotor: "",
        vehicleRatePerKm: "",
      }));
    }
  };

  // Se digito una targa già presente → auto-seleziona il veicolo
  useEffect(() => {
    const lp = normalizePlate(formValues.licensePlate);
    if (!lp || vehicles.length === 0) return;
    const v = vehicles.find((x) => normalizePlate(x.license_plate) === lp);
    if (v) {
      setSelectedVehicleId(String(v.id));
      setFormValues((prev) => ({
        ...prev,
        vehicleBrand: v.vehicle_brand || "",
        vehicleType: v.vehicle_type || "",
        vehicleMotor: v.vehicle_motor || "",
        vehicleRatePerKm: v.rate_per_km != null ? String(v.rate_per_km) : "",
      }));
    }
  }, [formValues.licensePlate, vehicles]);

  // -------- Submit --------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      let vehicleId = selectedVehicleId;

      // 1) crea veicolo se non selezionato
      if (!vehicleId) {
        try {
          const createdVehicle = await companyVehicleCreate(token, {
            employee: Number(selectedEmployee.id),
            license_plate: normalizePlate(formValues.licensePlate),
            vehicle_brand: (formValues.vehicleBrand || '').trim(),
            vehicle_type: (formValues.vehicleType || '').trim(),
            vehicle_motor: formValues.vehicleMotor || '',
            rate_per_km: formValues.vehicleRatePerKm !== '' ? formValues.vehicleRatePerKm : null,
          });
          vehicleId = String(createdVehicle.id);
          setVehicles(prev => [createdVehicle, ...prev]);
          setSelectedVehicleId(vehicleId);
        } catch (err) {
          // Mostra SOLO i messaggi del backend, senza reset
          let msg = "Errore durante la creazione del veicolo.";
          const data = err?.response?.data;
          if (data) {
            if (typeof data === 'string') {
              msg = data;
            } else if (Array.isArray(data)) {
              msg = data.join('\n');
            } else if (typeof data === 'object') {
              const parts = [];
              Object.values(data).forEach((v) => {
                if (Array.isArray(v)) parts.push(v.join(', '));
                else if (typeof v === 'string') parts.push(v);
                else parts.push(JSON.stringify(v));
              });
              if (parts.length) msg = parts.join('\n');
            }
          }
          await Swal.fire('Errore', msg, 'error');
          return;
        }
      }

      // 2) payload richiesta mileage
      const fd = new FormData();
      fd.append("employee_id", String(selectedEmployee.id));
      fd.append("title", formValues.title);
      fd.append("date", formValues.date);
      fd.append("departure_location", formValues.departureAddress);
      fd.append("arrival_location", formValues.arrivalAddress);
      fd.append("distance_km", String(formValues.distanceKm));
      fd.append("description", formValues.description || "");
      fd.append("type", "mileage");
      fd.append("vehicle", String(vehicleId));
      if (formValues.requestRatePerKm !== '') {
        fd.append("rate_per_km", String(formValues.requestRatePerKm));
      }
      if (file) fd.append("file_binary", file);

      let created;
      try {
        created = await submitForm(fd);
      } catch (err) {
        let msg = 'Errore durante il salvataggio.';
        const data = err?.response?.data;
        if (data) {
          if (typeof data === 'string') {
            msg = data;
          } else if (Array.isArray(data)) {
            msg = data.join('\n');
          } else if (typeof data === 'object') {
            const parts = [];
            Object.values(data).forEach((v) => {
              if (Array.isArray(v)) parts.push(v.join(', '));
              else if (typeof v === 'string') parts.push(v);
              else parts.push(JSON.stringify(v));
            });
            if (parts.length) msg = parts.join('\n');
          }
        }
        await Swal.fire('Errore', msg, 'error');
        return;
      }

      const data = created?.data ?? created;
      if (!data || !data.id) {
        const msg = data?.error || data?.message || 'Creazione non riuscita. Riprova.';
        await Swal.fire('Errore', msg, 'error');
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Successo!",
        text: "La richiesta è stata creata correttamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      afterSubmit?.();

      // reset SOLO dopo successo
      setFormValues({
        title: '',
        date: '',
        vehicleBrand: '',
        vehicleType: '',
        vehicleMotor: '',
        licensePlate: '',
        vehicleRatePerKm: '',
        requestRatePerKm: '',
        departureAddress: '',
        arrivalAddress: '',
        distanceKm: '',
        description: '',
      });
      setFile(null);
      setSelectedVehicleId("");

    } catch (error) {
      let msg = 'Errore durante il salvataggio.';
      const data = error?.response?.data;
      if (data) {
        if (typeof data === 'string') msg = data;
        else if (Array.isArray(data)) msg = data.join('\n');
        else if (typeof data === 'object') {
          const parts = [];
          Object.values(data).forEach((v) => {
            if (Array.isArray(v)) parts.push(v.join(', '));
            else if (typeof v === 'string') parts.push(v);
            else parts.push(JSON.stringify(v));
          });
          if (parts.length) msg = parts.join('\n');
        }
      }
      await Swal.fire('Errore', msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach { display: none; }
    .trix-button-group.trix-button-group--file-tools { display: none; }
  `;

  const vehicleInputsDisabled = Boolean(selectedVehicleId);

  const selectedEmployeesMemo = useMemo(
    () => (selectedEmployee?.id ? [selectedEmployee] : []),
    [selectedEmployee?.id]
  );

  const employeeRolePairsMemo = useMemo(
    () => (selectedEmployee?.id ? [{ employeeId: selectedEmployee.id }] : []),
    [selectedEmployee?.id]
  );

  const handleEmployeesChange = useCallback((list) => {
    const emp = list[0] || null;
    setSelectedEmployee(emp || { id: "", name: "" });
    setFormErrors((prev) => ({ ...prev, employee: "" }));
  }, []);

  return (
    <div>
      <style>{css}</style>
      <form onSubmit={handleSubmit} className="px-4 pt-6 mb-4 bg-white">
        {/* Dipendente */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Dipendente</label>
          <ListBoxQueryMultiSelectEmployeesActiveContract
            token={token}
            selectedEmployees={selectedEmployeesMemo}
            employeeRolePairs={employeeRolePairsMemo}
            onChange={handleEmployeesChange}
            disabled={!isEmployeeSelected}
            placeholder="Seleziona dipendente"
            filterBy="hr_active"
            wrapperClassName="border border-gray-300 rounded-md"
            buttonClassName="border-none shadow-none"
          />
          {formErrors.employee && <p className="text-red-500 text-xs mt-1">{formErrors.employee}</p>}
        </div>

        {/* Veicolo associato (Listbox) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Veicolo associato <span className="text-[12px] text-gray-400"> - (Per salvare un nuovo veicolo compila i campi nella richiesta)</span> </label>
          
          <Listbox value={selectedVehicleOption} onChange={onPickVehicle} disabled={!selectedEmployee?.id}>
            <div className="relative mt-1">
              <Listbox.Button
                className={`relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none sm:text-sm ${!selectedEmployee?.id ? "bg-gray-100 text-gray-500" : ""}`}
              >
                <span className="block truncate">
                  {selectedVehicleOption?.name || vehiclePlaceholder.name}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                  {vehicleOptions.map((opt) => (
                    <Listbox.Option
                      key={opt.id || "placeholder"}
                      className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-indigo-100 text-gray-900" : "text-gray-900"}`}
                      value={opt}
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{opt.name}</span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          {!selectedEmployee?.id && (
            <p className="text-xs text-gray-500 mt-1">Seleziona prima un dipendente per vedere i veicoli.</p>
          )}
        </div>

        {/* Campi standard + campi NUOVO veicolo (disabilitati quando scegli un veicolo esistente) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Targa */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Targa {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text" name="licensePlate" value={formValues.licensePlate} onChange={handleFieldChange}
              disabled={Boolean(selectedVehicleId) || !isEmployeeSelected}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm uppercase placeholder:text-gray-300 ${selectedVehicleId ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'}`}
              placeholder="AA123AA"
            />
            {formErrors.licensePlate && <p className="text-red-500 text-xs mt-1">{formErrors.licensePlate}</p>}
          </div>

          {/* Marca */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Marca {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text" name="vehicleBrand" value={formValues.vehicleBrand} onChange={handleFieldChange}
              disabled={Boolean(selectedVehicleId) || !isEmployeeSelected}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${selectedVehicleId ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'}`}
            />
            {formErrors.vehicleBrand && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleBrand}</p>}
          </div>

          {/* Modello */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Modello {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text" name="vehicleType" value={formValues.vehicleType} onChange={handleFieldChange}
              disabled={Boolean(selectedVehicleId) || !isEmployeeSelected}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${selectedVehicleId ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'}`}
            />
            {formErrors.vehicleType && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleType}</p>}
          </div>

          {/* Alimentazione */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Alimentazione {selectedVehicleId ? '' : '*'}
            </label>

            <Listbox
              value={formValues.vehicleMotor || ''}
              onChange={(val) => handleFieldChange({ target: { name: 'vehicleMotor', value: val } })}
              disabled={Boolean(selectedVehicleId) || !isEmployeeSelected}
            >
              {({ open, disabled }) => (
                <div className="relative mt-1">
                  {/* Button */}
                    <Listbox.Button
                      className={[
                        'relative w-full rounded-md border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none',
                        disabled
                          ? (selectedVehicleId
                              ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600'
                              : 'bg-white cursor-not-allowed border-gray-300 text-gray-400'
                            )
                          : 'bg-white border-gray-300'
                      ].join(' ')}
                    >
                    <span className="block truncate">
                      {formValues.vehicleMotor
                        ? (FUEL_CHOICES.find(([v]) => v === formValues.vehicleMotor)?.[1] || formValues.vehicleMotor)
                        : '—'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      {/* opzionale: icona caret */}
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>

                  {/* Options */}
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base
                                shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    >
                      {/* Opzione vuota */}
                      <Listbox.Option
                        key="__empty__"
                        value=""
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>—</span>
                            {selected ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>

                      {FUEL_CHOICES.map(([val, label]) => (
                        <Listbox.Option
                          key={val}
                          value={val}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{label}</span>
                              {selected ? (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>

            {formErrors.vehicleMotor && (
              <p className="text-red-500 text-xs mt-1">{formErrors.vehicleMotor}</p>
            )}
          </div>

          {/* Tariffa €/km ANAGRAFICA (solo in creazione veicolo) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Tariffa €/km (anagrafica veicolo)
              <span className="block text-xs text-transparent">-</span>
            </label>
            <input
              type="number" step="0.01" min="0" name="vehicleRatePerKm" value={formValues.vehicleRatePerKm} onChange={handleFieldChange}
              disabled={Boolean(selectedVehicleId) || !isEmployeeSelected}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm placeholder:text-gray-300 ${selectedVehicleId ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'}`}
              placeholder="es. 0.35"
            />
          </div>

          {/* Tariffa €/km PER QUESTA RICHIESTA (override) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tariffa €/km (per questa richiesta)
              <span className="block text-xs text-gray-500">Lascia vuoto per usare la tariffa del veicolo</span>
            </label>
            <input
              type="number" step="0.01" min="0" name="requestRatePerKm" value={formValues.requestRatePerKm} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm placeholder:text-gray-300"
              placeholder="es. 0.35"
              disabled={!isEmployeeSelected}
            />
          </div>
          
          {/* Oggetto */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Oggetto *</label>
            <input
              type="text" name="title" value={formValues.title} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
              disabled={!isEmployeeSelected} 
            />
            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
          </div>

          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Data *</label>
            <input
              type="date" name="date" value={formValues.date} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
              disabled={!isEmployeeSelected}
            />
            {formErrors.date && <p className="text-red-500 text-xs mt-1">{formErrors.date}</p>}
          </div>

          {/* Partenza / Arrivo / Distanza */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Partenza *</label>
            <input
              type="text" name="departureAddress" value={formValues.departureAddress} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.departureAddress && <p className="text-red-500 text-xs mt-1">{formErrors.departureAddress}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Arrivo *</label>
            <input
              type="text" name="arrivalAddress" value={formValues.arrivalAddress} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.arrivalAddress && <p className="text-red-500 text-xs mt-1">{formErrors.arrivalAddress}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Distanza Km *</label>
            <input
              type="number" step="0.01" min="0" name="distanceKm" value={formValues.distanceKm} onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.distanceKm && <p className="text-red-500 text-xs mt-1">{formErrors.distanceKm}</p>}
          </div>
        </div>

        {/* Allegato della RICHIESTA */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allegato</label>
          {file ? (
            <div className="mb-2">
              <p className="text-sm text-gray-700">Nuovo file selezionato:</p>
              <ul className="text-sm text-gray-600 list-disc pl-5">
                <li><strong>Nome:</strong> {file.name}</li>
                <li><strong>Tipo:</strong> {file.type}</li>
                <li><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</li>
              </ul>
              <button type="button" onClick={handleDeleteFile} className="mt-1 text-red-500 text-sm font-semibold underline">Rimuovi</button>
            </div>
          ) : (
            <div {...getRootProps({ className: "border-dashed border-2 border-gray-300 p-4 rounded-md cursor-pointer text-center text-sm text-gray-500" })}>
              <input {...getInputProps()} />
              <p>Trascina qui il file o clicca per selezionarlo (max 2MB)</p>
            </div>
          )}
        </div>

        {/* Note */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive</label>
          <input type="hidden" id="description" name="description" ref={descriptionRef} value={formValues.description} onChange={() => {}} />
          <trix-editor
            input="description"
            onChange={(e) => setFormValues((prev) => ({ ...prev, description: e.target.innerHTML }))}
            onBlur={() => {
              const html = descriptionRef.current?.value;
              setFormValues((prev) => ({ ...prev, description: html || prev.description }));
            }}
          />
        </div>

        {/* Submit */}
        <div className="mt-6 flex justify-center">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
            <button type="submit" className="bg-indigo-500 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-600 focus:outline-none w-24 font-semibold">
              Invia
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default FormCreaRimborsoKmCompany;